// Этап 23: этапы проекта в ленте — чистые помощники без Svelte и сети.
//
// Карточка этапа в ленте строится по записи этапа (реш. 1): пока конспекта нет
// (pending/failed), в ленту подставляется заглушка с id `stage_<id>`; готовый
// конспект приходит сообщением role='summary' и заглушку вытесняет. Номер
// «Этап N» считается по позиции среди этапов чата и в текстах не хранится (реш. 17).

export const STAGE_PLACEHOLDER_PREFIX = 'stage_';
export const STAGE_HINT_STEP = 25;

const HINT_KEY_PREFIX = 'petrovich:stage-hint:';

export function isSummaryMessage(message) {
  return Boolean(message) && message.role === 'summary';
}

export function stageIdOfSummary(message) {
  if (!message) {
    return null;
  }
  if (message._stageId) {
    return message._stageId;
  }
  const requestId = message.requestId || '';
  return requestId.startsWith('stage:') ? requestId.slice('stage:'.length) : null;
}

export function stageNumber(stages, stageId) {
  const index = (stages || []).findIndex((stage) => stage.stageId === stageId);
  return index >= 0 ? index + 1 : null;
}

export function stageTitle(stage, number, fallback = 'Этап') {
  if (stage?.title) {
    return stage.title;
  }
  return number ? `${fallback} ${number}` : fallback;
}

// Метка «на микросекунду позже конца этапа» — заглушка встаёт туда же, куда сервер
// поставит конспект (ts конспекта = to_ts + 1 мкс), и порядок ленты не дёргается.
function placeholderTs(stage) {
  return stage.toTs || stage.fromTs || new Date().toISOString();
}

export function stagePlaceholder(stage) {
  return {
    id: `${STAGE_PLACEHOLDER_PREFIX}${stage.stageId}`,
    role: 'summary',
    text: '',
    ts: placeholderTs(stage),
    requestId: `stage:${stage.stageId}`,
    model: stage.model || '',
    stageId: null,
    _stageId: stage.stageId,
    _placeholder: true,
  };
}

// Лента с карточками: заглушки для этапов без конспекта, свёрнутые сообщения скрыты.
export function withStageCards(messages, stages) {
  const list = Array.isArray(messages) ? messages : [];
  const stageList = Array.isArray(stages) ? stages : [];
  if (!stageList.length) {
    return list.filter((message) => !message?._placeholder);
  }
  const present = new Set(list.filter(isSummaryMessage).filter((m) => !m._placeholder).map(stageIdOfSummary));
  const result = list.filter((message) => !message?._placeholder && !message?.stageId);
  for (const stage of stageList) {
    // Готовый этап представлен своим конспектом-сообщением (он может лежать в ещё не
    // загруженной части истории); заглушка нужна только пока конспекта нет.
    if (present.has(stage.stageId) || stage.status === 'ready') {
      continue;
    }
    result.push(stagePlaceholder(stage));
  }
  return result.sort((left, right) => {
    const l = Date.parse(left.ts || 0) || 0;
    const r = Date.parse(right.ts || 0) || 0;
    return l - r;
  });
}

// Событие `closed` (реш. 7): свернуть диапазон этапа в ленте немедленно.
export function foldMessagesForStage(messages, stage) {
  return (messages || []).filter((message) => {
    if (isSummaryMessage(message)) {
      return true;
    }
    if (!message?.ts) {
      return true;
    }
    return !(message.ts >= stage.fromTs && message.ts <= stage.toTs);
  });
}

// Событие `reopened`: убрать конспект и заглушку — сами сообщения вернутся с сервера.
export function dropStageFromMessages(messages, stageId) {
  return (messages || []).filter((message) => stageIdOfSummary(message) !== stageId);
}

export function upsertStage(stages, stage) {
  const list = Array.isArray(stages) ? [...stages] : [];
  const index = list.findIndex((item) => item.stageId === stage.stageId);
  if (index >= 0) {
    list[index] = { ...list[index], ...stage };
  } else {
    list.push(stage);
  }
  return list.sort((left, right) => (left.fromTs || '').localeCompare(right.fromTs || ''));
}

export function removeStage(stages, stageId) {
  return (stages || []).filter((item) => item.stageId !== stageId);
}

// Сброс контекста внутри свёрнутого диапазона в ленте не показывается.
export function resetInsideStage(stages, contextResetAt) {
  if (!contextResetAt) {
    return false;
  }
  return (stages || []).some((stage) => stage.fromTs <= contextResetAt && contextResetAt <= stage.toTs);
}

// ── Плашка «Начало этапа уже вне памяти Петровича» (реш. 9) ──

function readHintMark(chatId) {
  try {
    const raw = globalThis.localStorage?.getItem(`${HINT_KEY_PREFIX}${chatId}`);
    const value = raw == null ? NaN : Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch (_) {
    return null;
  }
}

export function dismissStageHint(chatId, liveCount) {
  try {
    globalThis.localStorage?.setItem(`${HINT_KEY_PREFIX}${chatId}`, String(liveCount));
  } catch (_) {
    // localStorage недоступен — плашка просто покажется снова.
  }
}

export function shouldShowStageHint({ chatId, liveCount, liveLimit, isOwner, isDacha }) {
  if (!isOwner || isDacha || !chatId) {
    return false;
  }
  if (!Number.isFinite(liveCount) || !Number.isFinite(liveLimit) || liveLimit <= 0) {
    return false;
  }
  if (liveCount < liveLimit) {
    return false;
  }
  const dismissedAt = readHintMark(chatId);
  if (dismissedAt == null) {
    return true;
  }
  return liveCount >= dismissedAt + STAGE_HINT_STEP;
}

// ── Ссылки на источники в поле правки (решение владельца 2026-09-22) ──

const SOURCE_LINK_RE = /\[#(\d+)\]\(msg:([^)\s]+)\)/g;

export function collapseSourceLinks(text) {
  return (text || '').replace(SOURCE_LINK_RE, '[#$1]');
}

export function sourceLinkMap(text) {
  const map = new Map();
  for (const match of (text || '').matchAll(SOURCE_LINK_RE)) {
    map.set(match[1], match[2]);
  }
  return map;
}

// Возвращает адреса ссылкам по номеру из исходного текста; номера без адреса остаются
// маркерами и ссылкой не становятся (новые ссылки руками не добавить).
export function expandSourceLinks(edited, original) {
  const map = sourceLinkMap(original);
  return (edited || '').replace(/\[#(\d+)\](?!\()/g, (whole, number) => {
    const messageId = map.get(number);
    return messageId ? `[#${number}](msg:${messageId})` : whole;
  });
}

// Текст для композера из хвостов этапа (реш. 26) — без отправки.
export function carryOverDraft(stage, number, prefixTemplate) {
  const items = Array.isArray(stage?.carryOver) ? stage.carryOver : [];
  const title = stageTitle(stage, number);
  const head = prefixTemplate.replace('{title}', title);
  if (!items.length) {
    return head;
  }
  return `${head}\n${items.map((item) => `- ${item}`).join('\n')}`;
}

export function formatStageRange(stage, locale = 'ru-RU') {
  const from = stage?.fromTs ? new Date(stage.fromTs) : null;
  const to = stage?.toTs ? new Date(stage.toTs) : null;
  const fmt = (date) => (date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' })
    : '');
  const a = fmt(from);
  const b = fmt(to);
  if (!a && !b) {
    return '';
  }
  return a === b ? a : `${a} — ${b}`;
}
